import {Assignment, ServiceType} from '@features/assignment/types.ts'
import {Trans, useTranslation, TKey} from '@/translations/i18n.tsx'
import {UploadButtons} from '../upload-buttons'
import {AlertTriangleIcon} from '@components/ui/icon'
import {WalkthroughFormButton} from '@features/assignment/components/walkthrough-form-button'
import {AssignmentVideoButtons} from '@features/assignment/components/assignment-video-buttons'
import {ThreeDScanReminderCard} from '@features/assignment/components/three-d-scan-reminder-card'
import {envVars} from '@/envVars.ts'
import {
    ASSIGNMENT_DESCRIPTION_PARAGRAPH_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_TEXT_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_TITLE_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_WRAPPER_CLASSNAME
} from '@features/assignment/constants'
import {cn} from '@utilities/helpers'

export const rejectedReasonsToTranslationKey = {
    empty_folder: 'assignment:descriptions:needs_more_assets:rejected_reasons:empty_folder',
    empty_form: 'assignment:descriptions:needs_more_assets:rejected_reasons:empty_form',
    exp_empty_folder: 'assignment:descriptions:needs_more_assets:rejected_reasons:exp_empty_folder',
    exp_individual_assets: 'assignment:descriptions:needs_more_assets:rejected_reasons:exp_individual_assets',
    exp_insufficient_assets: 'assignment:descriptions:needs_more_assets:rejected_reasons:exp_insufficient_assets',
    exp_low_quality: 'assignment:descriptions:needs_more_assets:rejected_reasons:exp_low_quality',
    exp_too_much_assets: 'assignment:descriptions:needs_more_assets:rejected_reasons:exp_too_much_assets',
    exp_too_shaky: 'assignment:descriptions:needs_more_assets:rejected_reasons:exp_too_shaky',
    exp_wrong_folder: 'assignment:descriptions:needs_more_assets:rejected_reasons:exp_wrong_folder',
    exp_wrong_ratio: 'assignment:descriptions:needs_more_assets:rejected_reasons:exp_wrong_ratio',
    manual_explanation: 'assignment:descriptions:needs_more_assets:rejected_reasons:manual_explanation',
    missing_assets: 'assignment:descriptions:needs_more_assets:rejected_reasons:missing_assets',
    not_enough_photos_form: 'assignment:descriptions:needs_more_assets:rejected_reasons:not_enough_photos_form',
    unmerged_brackets: 'assignment:descriptions:needs_more_assets:rejected_reasons:unmerged_brackets',
    wrong_assets: 'assignment:descriptions:needs_more_assets:rejected_reasons:wrong_assets',
    wrong_file_format: 'assignment:descriptions:needs_more_assets:rejected_reasons:wrong_file_format',
    wrong_listing_form: 'assignment:descriptions:needs_more_assets:rejected_reasons:wrong_listing_form'
} as const satisfies Record<NonNullable<Assignment['assets_rejected_reason']>, TKey>

export const NeedsMoreAssetsDescription = ({
    assignment,
    serviceType
}: {
    assignment: Assignment
    serviceType: ServiceType
}) => {
    const {t} = useTranslation()

    return (
        <div className="flex flex-col gap-6 w-full">
            <div className={ASSIGNMENT_DESCRIPTION_WRAPPER_CLASSNAME}>
                <div className={ASSIGNMENT_DESCRIPTION_TEXT_CLASSNAME}>
                    <h3 className={ASSIGNMENT_DESCRIPTION_TITLE_CLASSNAME}>
                        {t(`assignment:descriptions:needs_more_assets:title:${serviceType}` as TKey)}
                    </h3>
                    {assignment.assets_rejected_reason && (
                        <p
                            className={cn(
                                ASSIGNMENT_DESCRIPTION_PARAGRAPH_CLASSNAME,
                                'flex items-center justify-center gap-1 text-danger-600'
                            )}
                        >
                            <AlertTriangleIcon size={20} />
                            <span>{t(rejectedReasonsToTranslationKey[assignment.assets_rejected_reason])}</span>
                        </p>
                    )}
                    {assignment.frame_final_folder_review_link && (
                        <p className={ASSIGNMENT_DESCRIPTION_PARAGRAPH_CLASSNAME}>
                            <Trans
                                i18nKey="assignment:descriptions:needs_more_assets_link"
                                components={[
                                    <a
                                        href={assignment.frame_final_folder_review_link}
                                        target="_blank"
                                        rel="noreferrer"
                                    />
                                ]}
                            />
                        </p>
                    )}
                </div>

                {assignment.task_media.name == 'walkthrough' ? (
                    <WalkthroughFormButton assignment={assignment} />
                ) : assignment.task_category.name == 'Experiences' && assignment.task_media.name == 'video' ? (
                    <AssignmentVideoButtons assignment={assignment} />
                ) : (
                    <UploadButtons assignment={assignment} />
                )}
            </div>
            {envVars.VITE_IS_FLOOR_PLAN_ENABLED && assignment.is_3d_scan && (
                <ThreeDScanReminderCard assignment={assignment} />
            )}
        </div>
    )
}
